/*
 * Photos are imported through vite-imagetools (see vite.config.ts). Every one
 * of them renders inside a card, a gallery tile or a sidebar, none of which is
 * ever wider than ~520 CSS px, so 800w covers a 2x screen with room to spare.
 * Deliberately no larger variant: a 3x phone would download it for a ~350px
 * tile and pay for pixels it cannot show.
 */
import driveway2Img from 'assets/driveway_2.webp?w=400;800&format=webp&quality=70&as=picture';
import drivewayImg from 'assets/driveway.webp?w=400;800&format=webp&quality=70&as=picture';
import fenceImg from 'assets/fence.webp?w=400;800&format=webp&quality=70&as=picture';
import foundationsSlabImg from 'assets/foundations_slab.webp?w=400;800&format=webp&quality=70&as=picture';
import foundationsImg from 'assets/foundations.webp?w=400;800&format=webp&quality=70&as=picture';
import landscapeImg from 'assets/landscape.webp?w=400;800&format=webp&quality=70&as=picture';
import patioImg from 'assets/patio.webp?w=400;800&format=webp&quality=70&as=picture';
import posterImg from 'assets/poster.webp?w=400;800&format=webp&quality=70&as=picture';
import repair2Img from 'assets/repair_2.webp?w=400;800&format=webp&quality=70&as=picture';
import repairImg from 'assets/repair.webp?w=400;800&format=webp&quality=70&as=picture';

export const COMPANY = {
  name: 'Dreams Built',
  legalName: 'Dreams Built Ltd',
  phone: '021 412 384',
  phoneHref: 'tel:+6421412384',
  email: 'admin@dreamsbuilt.co.nz',
  emailHref: 'mailto:admin@dreamsbuilt.co.nz',
  foundingYear: 2015,
  hours: 'Mon–Fri, 7am–5pm',
  // Google Business Profile, and the link that opens its "write a review" box.
  googleProfileUrl: 'https://g.page/r/CbuJfyKs9LNSEBM',
  googleReviewUrl: 'https://g.page/r/CbuJfyKs9LNSEBM/review',
  facebookUrl: 'https://www.facebook.com/profile.php?id=61556609252800',
  linkedinUrl: 'https://www.linkedin.com/company/dreams-built',
  serviceAreas: ['Hamilton', 'Cambridge', 'Morrinsville', 'Te Awamutu', 'wider Waikato']
} as const;

export const STATS = [
  { value: '600+', label: 'foundations poured' },
  { value: '10+', label: 'years in the ground' },
  { value: '7', label: 'group builders supplied' },
  { value: '48h', label: 'typical quote turnaround' }
] as const;

export type ServicePage = {
  slug: string;
  /** Short name used in cards/nav */
  name: string;
  /** H1 on the service page */
  heading: string;
  /** Card + meta description */
  summary: string;
  /** Longer intro paragraph on the service page */
  intro: string;
  image: ImagetoolsPicture;
  imageAlt: string;
  /** "What's included" bullets */
  includes: string[];
  faqs: Array<{ question: string; answer: string }>;
};

export const SERVICES: ServicePage[] = [
  {
    slug: 'residential-foundations',
    name: 'Residential foundations',
    heading: 'Concrete house foundations across Hamilton & Waikato',
    summary:
      'Engineered slabs, footings and pod floors for new homes, poured flat, square and on schedule for group builders and owner-builders.',
    intro:
      'A house is only as good as the floor it stands on. We set out, box, and pour residential foundations to the engineer’s drawings, including raft and pod slab systems, and we hand them over inspection-ready. Group builders across the Waikato trust us with their programmes because our floors go down flat, square, and on the day we said they would.',
    image: foundationsImg,
    imageAlt: 'Boxed residential foundation ready for a concrete pour',
    includes: [
      'Set-out from your plans, checked against the engineer’s drawings',
      'Boxing, mesh and reinforcing placed to spec',
      'Raft, waffle-pod and conventional slab systems',
      'Plumbing and services coordinated before the pour',
      'Machine-finished surface, cured and ready for framing',
      'Council and engineer inspections handled with you'
    ],
    faqs: [
      {
        question: 'How long does a house foundation take?',
        answer:
          'For a typical new home, allow one to two weeks from set-out to a cured, buildable floor, depending on weather, inspections and the slab system specified. We work in with your build programme and confirm dates before we start.'
      },
      {
        question: 'Do you work with group builders?',
        answer:
          'Yes, most of our work is for group home builders across Hamilton and the Waikato. We can price from your standard plans and slot into your schedule.'
      },
      {
        question: 'Can you price straight from our plans?',
        answer:
          'Send us the floor plan and engineering drawings and we’ll come back with a fixed quote, usually within two working days.'
      }
    ]
  },
  {
    slug: 'commercial-foundations',
    name: 'Commercial foundations',
    heading: 'Commercial & light-industrial concrete foundations',
    summary:
      'Foundations and floor slabs for commercial buildings, warehouses and multi-unit developments: engineered, sequenced and poured to programme.',
    intro:
      'Bigger floors, tighter tolerances, less room for delay. We pour foundations and slabs for commercial buildings, warehouses and multi-unit residential projects across the Waikato, coordinating with your engineer, project manager and other trades so the pour lands exactly where the programme needs it.',
    image: foundationsSlabImg,
    imageAlt: 'Freshly finished concrete floor slab with the boxing still in place',
    includes: [
      'Floor slabs, strip and pad footings to engineering spec',
      'Multi-unit and terraced housing foundations',
      'Sequenced pours coordinated around your programme',
      'Laser-screeded, machine-finished floors',
      'As-built records and inspection sign-off support'
    ],
    faqs: [
      {
        question: 'What size commercial jobs do you take on?',
        answer:
          'From single commercial units and duplexes up to warehouse floors and multi-unit developments. If you’re unsure whether a job is our size, send the drawings through and we’ll tell you straight.'
      },
      {
        question: 'Can you work around other trades and staged programmes?',
        answer:
          'Yes. Staged and sequenced pours are normal for us. We plan the boxing, reinforcing and pour order around your programme and keep you informed at each hold point.'
      }
    ]
  },
  {
    slug: 'concrete-driveways',
    name: 'Concrete driveways',
    summary:
      'Plain, coloured and exposed-aggregate driveways built to last decades: properly based, reinforced, cut and cured.',
    heading: 'Concrete driveways in Hamilton & Waikato',
    intro:
      'A driveway is poured once and driven on for thirty years, so it’s worth doing properly. We excavate, base, box and pour driveways in plain, coloured and exposed-aggregate finishes, with the reinforcing, joint cuts and curing that stop them cracking and scaling down the line.',
    image: drivewayImg,
    imageAlt: 'New exposed-aggregate concrete driveway',
    includes: [
      'Excavation, basecourse and compaction',
      'Plain, coloured and exposed-aggregate finishes',
      'Steel mesh reinforcing as standard',
      'Saw cuts and expansion joints placed to control cracking',
      'Vehicle crossings and council crossing applications',
      'Old driveway removal and disposal'
    ],
    faqs: [
      {
        question: 'How much does a concrete driveway cost?',
        answer:
          'It depends on area, access, excavation and the finish you choose. Exposed aggregate costs more than plain grey. Send us the address and rough dimensions and we’ll give you a fixed quote, free.'
      },
      {
        question: 'How long before we can drive on it?',
        answer:
          'Foot traffic after about two days; vehicles after seven. Concrete keeps gaining strength for weeks, so we’ll give you honest timeframes for your specific pour and weather.'
      },
      {
        question: 'What finishes are available?',
        answer:
          'Plain grey, coloured concrete (oxide through the mix), and exposed aggregate in a range of stone blends. We can show you examples of each from jobs around Hamilton and the Waikato.'
      }
    ]
  },
  {
    slug: 'patios-and-paths',
    name: 'Patios & paths',
    heading: 'Concrete patios, paths & outdoor areas',
    summary:
      'Outdoor living areas, paths and pads with falls that drain, finishes that last, and edges that look sharp against your landscaping.',
    intro:
      'Patios and paths look simple until they pond water or crack at the corners. We form and pour outdoor concrete with proper falls, reinforcing and joint layout, in plain, coloured or exposed-aggregate finishes that tie into your home and landscaping.',
    image: patioImg,
    imageAlt: 'Concrete patio and outdoor living area',
    includes: [
      'Patios, paths, porches and shed pads',
      'Falls set for drainage, so no ponding',
      'Plain, coloured and exposed-aggregate finishes',
      'Non-slip broom and salt finishes for wet areas',
      'Tie-ins to lawns, decks and garden edges'
    ],
    faqs: [
      {
        question: 'Can you match an existing concrete finish?',
        answer:
          'Usually, yes. New concrete always starts brighter, but we can match the finish and aggregate blend so it blends in as it weathers.'
      },
      {
        question: 'Do small jobs interest you?',
        answer:
          'Yes. Paths, pads and patios fit between our bigger pours, so small jobs still get scheduled promptly.'
      }
    ]
  },
  {
    slug: 'foundation-repair',
    name: 'Foundation repair',
    heading: 'Foundation repair & re-levelling',
    summary:
      'Honest assessment and repair of cracked, settled or damaged concrete foundations, from crack injection to cut-out and re-pour.',
    intro:
      'Not every cracked slab is a disaster, and not every “minor” crack is minor. We assess settled, cracked or damaged foundations honestly, tell you what actually needs doing, and carry out the repair: from sealing and crack injection through to cutting out and re-pouring failed sections.',
    image: repairImg,
    imageAlt: 'Concrete foundation repair work in progress',
    includes: [
      'On-site assessment and straight answers',
      'Crack repair and injection',
      'Cut-out and re-pour of failed sections',
      'Levelling of settled slabs and paths',
      'Work coordinated with engineers where required'
    ],
    faqs: [
      {
        question: 'Is a crack in my slab serious?',
        answer:
          'Hairline shrinkage cracks are normal and mostly cosmetic. Cracks that are widening, stepped, or letting water through need a proper look. We’ll tell you honestly which kind you have, and an assessment costs you nothing.'
      },
      {
        question: 'Do you provide reports for insurance?',
        answer:
          'We can document the damage and repair with photos and a written scope, and work alongside your insurer or engineer where needed.'
      }
    ]
  },
  {
    slug: 'fencing-and-hard-landscaping',
    name: 'Fencing & hard landscaping',
    heading: 'Fencing & hard landscaping to finish the job',
    summary:
      'Timber fences, retaining walls, paving and paths that finish your section properly, built by the same crew that poured your concrete.',
    intro:
      'The last ten percent of a build is what you look at every day. We build timber fences, retaining walls, paved areas and paths that finish your section off, often in the same visit as your driveway or patio, with one crew and one invoice.',
    image: fenceImg,
    imageAlt: 'New timber fence and hard landscaping',
    includes: [
      'Timber boundary and feature fences',
      'Gates and letterbox posts',
      'Retaining walls',
      'Paving, paths and gravelled areas',
      'Section tidy-ups after building work'
    ],
    faqs: [
      {
        question: 'Do you only do fencing with concrete work?',
        answer:
          'No, we take on standalone fencing and hard-landscaping jobs across Hamilton and the Waikato too. It’s often easiest to bundle it with a driveway or patio, but it’s not required.'
      },
      {
        question: 'What counts as hard landscaping?',
        answer:
          'The built parts of your outdoor space: retaining walls, paving, paths, edging and gravelled areas. We don’t do planting. We make sure everything the plants go around is solid, level and draining properly.'
      }
    ]
  }
];

export const TESTIMONIALS = [
  {
    quote:
      "Dreams Built transformed our property with a beautiful new driveway and patio. The team was professional, the work was completed on time, and the quality is outstanding. Couldn't be happier!",
    name: 'Hannah V.',
    location: 'Hamilton',
    job: 'Driveway & patio'
  },
  {
    quote:
      'When we needed a solid foundation for our new home, Dreams Built delivered beyond our expectations. Their attention to detail and expertise gave us confidence throughout the entire building process.',
    name: 'Kelly S.',
    location: 'Morrinsville',
    job: 'House foundation'
  },
  {
    quote:
      'The team at Dreams Built exceeded our expectations with their professionalism and quality of work. Our new patio looks fantastic! We highly recommend them for any concrete work.',
    name: 'John E.',
    location: 'Hamilton',
    job: 'Patio'
  }
] as const;

export const HOME_FAQS = [
  {
    question: 'What areas do you cover?',
    answer:
      'Hamilton, Cambridge, Morrinsville, Te Awamutu and the wider Waikato. For larger foundation contracts we travel further, so ask us.'
  },
  {
    question: 'How fast can you quote?',
    answer:
      'Send us your plans or measurements and we typically have a fixed quote back to you within two working days.'
  },
  {
    question: 'Do you work with group home builders?',
    answer:
      'Yes, most of our foundation work is for group home builders across the Waikato. We price from your standard plans and work in with your schedule.'
  },
  {
    question: 'Are you insured and inspection-ready?',
    answer:
      'Fully insured, and every foundation is poured to the engineer’s drawings with council and engineer inspections coordinated as part of the job.'
  }
] as const;

export const GALLERY = [
  { image: foundationsImg, alt: 'Residential foundation boxed and ready to pour', label: 'Residential foundation - set-out and boxing' },
  { image: foundationsSlabImg, alt: 'Freshly finished concrete floor slab', label: 'Floor slab - machine finished' },
  { image: drivewayImg, alt: 'New concrete driveway', label: 'Driveway - broom finish' },
  { image: driveway2Img, alt: 'Exposed aggregate driveway', label: 'Driveway - exposed aggregate' },
  { image: patioImg, alt: 'Concrete patio and outdoor area', label: 'Patio - outdoor living' },
  { image: fenceImg, alt: 'Timber fence', label: 'Fencing - timber boundary' },
  { image: landscapeImg, alt: 'Finished landscaping', label: 'Landscaping - section finish' },
  { image: repairImg, alt: 'Foundation repair', label: 'Repair - cut-out and re-pour' },
  { image: repair2Img, alt: 'Slab repair work', label: 'Repair - re-level' },
  { image: posterImg, alt: 'Concrete pour in progress', label: 'On the pour' }
] as const;
